import { Space, Tabs, Timeline, Typography } from 'antd';
import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { useUserProfileQuery } from '../../../features/auth/authApi';
import Lesson from '../components/Lesson';
import { useGetCourseDetailQuery } from '../../../features/courses/coursesApi';
import { ComponentNotebookResource } from '../../components/components/componentResources';
import MyLearningPanel from '../../analytics/containers/MyLearningPanel';
import CourseStudentsTable from '../../students/containers/studentsList';
import { isStudent, isTeacher } from '../../../helpers/permissions';

const { Title, Text } = Typography;

const CourseDetail = () => {
    const { t } = useTranslation();
    const { courseId } = useParams();
    const { data: user } = useUserProfileQuery('userDetails');
    const { data: course } = useGetCourseDetailQuery(courseId);
    const [currentLesson, setCurrentLesson] = React.useState('');
    const [completedLessons, setCompletedLessons] = React.useState([]);

    useEffect(() => {
        if (course) {
            // A teacher viewing their own course has no enrollment/progress, so the
            // payload omits these — default to empty so the timeline doesn't crash.
            setCurrentLesson(course.current_lesson || '');
            setCompletedLessons(course.completed_lessons || []);
        }
    }, [course]);

    const getStatusColor = (lesson) => {
        if (completedLessons.includes(lesson.id)) {
            return 'green';
        } else if (lesson.id === currentLesson) {
            return 'blue';
        } else {
            return 'grey';
        }
    };

    const getDot = (lesson) => {
        if (lesson) {
            if (completedLessons.includes(lesson.id)) {
                return <TaskAltIcon />;
            } else if (lesson.id === currentLesson) {
                return <ArrowForwardOutlinedIcon />;
            }
        } else if (lesson.is_available) {
            return <BlockOutlinedIcon />;
        }
    };

    // Teachers/admins aren't enrolled, so they have no progress to gate on: unlock the
    // whole course for them, and hide the per-lesson "Przesłane Prace" panel (it shows
    // the viewer's own submissions, which a teacher never has).
    const canManage = !!user && isTeacher(user);

    const timelineItems = course?.lessons?.map((lesson) => {
        return {
            color: getStatusColor(lesson),
            children: (
                <Lesson
                    lesson={lesson}
                    isCurrentLesson={lesson.id === currentLesson}
                    userId={user.pk}
                    unlocked={canManage}
                    showSubmissions={!canManage}
                />
            ),
            dot: getDot(lesson),
        };
    });

    const courseContent = (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Timeline items={timelineItems} />
            <ComponentNotebookResource/>
        </Space>
    );

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
                <Title level={1} style={{ marginBottom: 0 }}>{course?.title}</Title>
                {course?.author_name && (
                    <Text type="secondary">
                        {t('courses.instructor')}: {course.author_name}
                    </Text>
                )}
            </div>
            {user && isStudent(user) && <MyLearningPanel courseId={courseId} />}
            {user && isTeacher(user) ? (
                <Tabs
                    defaultActiveKey="students"
                    items={[
                        {
                            key: 'students',
                            label: t('courses.tabs.students'),
                            children: <CourseStudentsTable courseId={courseId} />,
                        },
                        {
                            key: 'overview',
                            label: t('courses.tabs.overview'),
                            children: courseContent,
                        },
                    ]}
                />
            ) : (
                courseContent
            )}
        </Space>
    );
};

export default CourseDetail;
